import { createEffect, createMemo, createSignal, For, Show, untrack } from "solid-js";
import { Link } from "@tanstack/solid-router";
import { BarChart3, CalendarRange, ChefHat, ChevronLeft, ChevronRight } from "@fleet/icons";
import type { MessageDescriptor } from "@lingui/core";
import { msg } from "@lingui/core/macro";

import { Card, CardContent, CardHeader, CardTitle } from "@fleet/ui";
import { Button } from "@/components/ui/button";
import { BarChart, type BarDatum } from "@/components/ui/bar-chart";
import { EmptyPlanIllustration } from "@/components/ui/illustrations";
import { GamificationPanel } from "./gamification-panel";
import {
  addDays,
  isDateKey,
  MEAL_TYPES,
  type MealType,
  startOfWeek,
  toDateKey,
  weekDateKeys,
} from "@mealplan/shared";
import { useMealPlanWeekQuery, usePantryQuery, useRecipesQuery } from "@mealplan/shared/solid";
import { usePowerSyncContext } from "@/providers/power-sync-context";
import { useDrizzle } from "@/hooks/use-drizzle";
import { useSession } from "@/lib/auth";
import { useI18n } from "@/providers/i18n-provider";
import { OFFLINE_CACHE_STALE_TIME_MS } from "@/lib/constants";

const DAYS_PER_WEEK = 7;
const TOP_RECIPES_LIMIT = 6;

/**
 * Monday of week containing `key` (`YYYY-MM-DD`). Normalizing to week start
 * makes deep-link idempotent: any day of the week resolves to the
 * same canonical URL. An invalid key falls back to current week.
 */
function weekStartFromKey(key: string | undefined): Date {
  if (key && isDateKey(key)) {
    const [y = 0, m = 1, d = 1] = key.split("-").map(Number);
    return startOfWeek(new Date(y, m - 1, d));
  }
  return startOfWeek(new Date());
}

interface InsightsViewProps {
  /** Initial week from URL (deep-link `?week=YYYY-MM-DD`). */
  initialWeek?: string;
  /** Reflects active week to URL (idempotent, `replace`). */
  onWeekChange?: (weekStartKey: string) => void;
}

export function InsightsView(props: InsightsViewProps = {}) {
  const { t, locale } = useI18n();
  const { db } = usePowerSyncContext();
  const drizzle = useDrizzle();
  const session = useSession();

  // The displayed week is deep-linkable (`?week=`) and idempotent, just like the
  // planner: the URL is the source of truth when loading/sharing.
  const [weekStartDate, setWeekStartDate] = createSignal(weekStartFromKey(props.initialWeek));

  // If the URL changes externally (back/forward, pasted link), reflect in the signal.
  createEffect(() => {
    const next = weekStartFromKey(props.initialWeek);
    if (toDateKey(next) !== toDateKey(untrack(weekStartDate))) {
      setWeekStartDate(next);
    }
  });

  // Reflects the active week to the URL whenever it changes (prev/next buttons).
  createEffect(() => {
    props.onWeekChange?.(toDateKey(weekStartDate()));
  });

  const weekKeys = createMemo(() => weekDateKeys(weekStartDate()));
  const weekStartKey = createMemo(() => weekKeys()[0] ?? toDateKey(weekStartDate()));
  const weekEndKey = createMemo(() => weekKeys().at(-1) ?? weekStartKey());

  const entries = useMealPlanWeekQuery({
    db,
    drizzle,
    userId: session.userId,
    startDate: weekStartKey,
    endDate: weekEndKey,
    staleTime: OFFLINE_CACHE_STALE_TIME_MS,
  });

  const recipes = useRecipesQuery({
    db,
    drizzle,
    userId: session.userId,
    staleTime: OFFLINE_CACHE_STALE_TIME_MS,
  });

  const pantry = usePantryQuery({
    db,
    drizzle,
    userId: session.userId,
    staleTime: OFFLINE_CACHE_STALE_TIME_MS,
  });

  const mealLabels: Record<MealType, MessageDescriptor> = {
    breakfast: msg`Breakfast`,
    lunch: msg`Lunch`,
    dinner: msg`Dinner`,
    snack: msg`Snack`,
  };

  const shortDay = (dateKey: string): string => {
    const [y = 0, m = 1, d = 1] = dateKey.split("-").map(Number);
    return new Date(y, m - 1, d).toLocaleDateString(locale(), { weekday: "short" });
  };

  const totalEntries = createMemo(() => (entries.data ?? []).length);

  // Planned meals per day (columns).
  const perDay = createMemo<BarDatum[]>(() => {
    const counts = new Map<string, number>();
    for (const e of entries.data ?? []) {
      if (e.plan_date) {
        counts.set(e.plan_date, (counts.get(e.plan_date) ?? 0) + 1);
      }
    }
    return weekKeys().map((key) => ({
      label: shortDay(key),
      value: counts.get(key) ?? 0,
      srLabel: key,
    }));
  });

  // Distribution by meal type (horizontal).
  const perMealType = createMemo<BarDatum[]>(() => {
    const counts = new Map<string, number>();
    for (const e of entries.data ?? []) {
      counts.set(e.meal_type ?? "", (counts.get(e.meal_type ?? "") ?? 0) + 1);
    }
    return MEAL_TYPES.map((meal) => ({
      label: t(mealLabels[meal]),
      value: counts.get(meal) ?? 0,
    }));
  });

  // Most planned recipes of the week (horizontal, top N).
  const topRecipes = createMemo<BarDatum[]>(() => {
    const counts = new Map<string, number>();
    for (const e of entries.data ?? []) {
      const name = e.recipe_name;
      if (name) {
        counts.set(name, (counts.get(name) ?? 0) + 1);
      }
    }
    return [...counts.entries()]
      .map(([label, value]) => ({ label, value }))
      .toSorted((a, b) => b.value - a.value)
      .slice(0, TOP_RECIPES_LIMIT);
  });

  const recipeCount = createMemo(() => (recipes.data ?? []).length);
  const pantryCount = createMemo(() => (pantry.data ?? []).length);

  const stats = createMemo(() => [
    { label: t(msg`Meals planned`), value: totalEntries(), icon: CalendarRange },
    { label: t(msg`Recipes`), value: recipeCount(), icon: ChefHat },
    { label: t(msg`Pantry items`), value: pantryCount(), icon: BarChart3 },
  ]);

  return (
    <div class="space-y-6">
      <div class="flex flex-wrap items-center justify-between gap-3">
        <h2 class="flex items-center gap-2 font-serif text-2xl font-semibold tracking-tight">
          <BarChart3 class="h-6 w-6 text-accent" />
          {t(msg`Insights`)}
        </h2>
        <div class="flex items-center gap-2">
          <Button
            variant="outline"
            size="sm"
            aria-label={t(msg`Previous week`)}
            onClick={() => setWeekStartDate(addDays(weekStartDate(), -DAYS_PER_WEEK))}
          >
            <ChevronLeft class="h-4 w-4" />
          </Button>
          <span class="text-sm font-medium tabular-nums" data-testid="insights-week-label">
            {weekStartKey()}
          </span>
          <Button
            variant="outline"
            size="sm"
            aria-label={t(msg`Next week`)}
            onClick={() => setWeekStartDate(addDays(weekStartDate(), DAYS_PER_WEEK))}
            data-testid="insights-next-week"
          >
            <ChevronRight class="h-4 w-4" />
          </Button>
        </div>
      </div>

      <GamificationPanel />

      {/* Resumen / informe numérico */}
      <div class="grid gap-3 sm:grid-cols-3">
        <For each={stats()}>
          {(s) => (
            <Card>
              <CardContent class="flex items-center gap-3 p-4">
                <div class="flex h-10 w-10 shrink-0 items-center justify-center rounded-xl bg-accent/10 text-accent">
                  <s.icon class="h-5 w-5" />
                </div>
                <div class="min-w-0">
                  <p class="text-2xl font-bold tabular-nums leading-none">{s.value}</p>
                  <p class="mt-1 truncate text-xs text-muted-foreground">{s.label}</p>
                </div>
              </CardContent>
            </Card>
          )}
        </For>
      </div>

      <Show
        when={totalEntries() > 0}
        fallback={
          <Show when={!entries.isLoading}>
            <div class="flex flex-col items-center justify-center rounded-2xl border border-dashed border-border bg-surface/40 p-12 text-center">
              <EmptyPlanIllustration class="mb-4 h-28 w-36 text-muted-foreground" />
              <h3 class="font-serif text-xl font-semibold">{t(msg`Nothing planned this week`)}</h3>
              <p class="mt-1 max-w-xs text-sm text-muted-foreground">
                {t(msg`Plan some meals to see your weekly insights here.`)}
              </p>
              <Link to="/planner" class="mt-5">
                <Button class="gap-2">
                  <CalendarRange class="h-4 w-4" />
                  {t(msg`Go to planner`)}
                </Button>
              </Link>
            </div>
          </Show>
        }
      >
        <div class="grid gap-4 lg:grid-cols-2">
          <Card>
            <CardHeader>
              <CardTitle class="text-base">{t(msg`Meals planned per day`)}</CardTitle>
            </CardHeader>
            <CardContent>
              <BarChart data={perDay()} caption={t(msg`Meals planned per day`)} />
            </CardContent>
          </Card>

          <Card>
            <CardHeader>
              <CardTitle class="text-base">{t(msg`By meal type`)}</CardTitle>
            </CardHeader>
            <CardContent>
              <BarChart
                data={perMealType()}
                caption={t(msg`Meals by type`)}
                orientation="horizontal"
              />
            </CardContent>
          </Card>

          <Card class="lg:col-span-2">
            <CardHeader>
              <CardTitle class="text-base">{t(msg`Most planned recipes`)}</CardTitle>
            </CardHeader>
            <CardContent>
              <Show
                when={topRecipes().length > 0}
                fallback={
                  <p class="text-sm text-muted-foreground">{t(msg`No recipes planned yet.`)}</p>
                }
              >
                <BarChart
                  data={topRecipes()}
                  caption={t(msg`Most planned recipes`)}
                  orientation="horizontal"
                />
              </Show>
            </CardContent>
          </Card>
        </div>
      </Show>
    </div>
  );
}
